import { useEffect, useMemo, useRef } from 'react';
import createDOMPurify from 'dompurify';
import withLgpdCookie from 'govbr.lgpd.plugin/components/withLgpdCookie';
import { useSelector } from 'react-redux';

const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const isTestEnvironment = () => {
  try {
    return (
      typeof process !== 'undefined' &&
      process?.env?.NODE_ENV &&
      process.env.NODE_ENV === 'test'
    );
  } catch (e) {
    return false;
  }
};

const hashString = (value) => {
  const str = typeof value === 'string' ? value : String(value ?? '');
  let hash = 5381;

  for (let i = 0; i < str.length; i++) {
    hash = (hash * 33) ^ str.charCodeAt(i);
  }

  // Converte para uint32 e depois base36 para ficar curto.
  return (hash >>> 0).toString(36);
};

/**
 * DOMPurify
 * --------
 * Aqui o objetivo é simples: depois de remover `<script>`, o HTML restante não pode
 * carregar execução ativa via handlers `on*` ou URLs tipo `javascript:`.
 *
 * Requisito operacional: não sabemos previamente quais tags/atributos serão usados
 * (ex.: web components como <df-messenger />), então a sanitização precisa permitir
 * custom elements e seus atributos (mantendo o bloqueio de vetores óbvios).
 */
let cachedDOMPurify = null;
let cachedDOMPurifyWindow = null;

const getSanitizer = () => {
  if (typeof window === 'undefined') return null;
  if (cachedDOMPurify && cachedDOMPurifyWindow === window)
    return cachedDOMPurify;

  try {
    cachedDOMPurify = createDOMPurify(window);
    cachedDOMPurifyWindow = window;
    return cachedDOMPurify;
  } catch (e) {
    cachedDOMPurify = null;
    cachedDOMPurifyWindow = null;
    return null;
  }
};

const sanitizeNonScriptHtml = (html) => {
  if (!isBrowser()) return '';
  if (typeof html !== 'string' || !html) return '';

  const DOMPurify = getSanitizer();

  if (!DOMPurify) return '';

  return DOMPurify.sanitize(html, {
    USE_PROFILES: { html: true },
    CUSTOM_ELEMENT_HANDLING: {
      tagNameCheck: /^[a-z][\w.-]*$/i,
      attributeNameCheck: /^[\w-.:]+$/i,
      allowCustomizedBuiltInElements: true,
    },
    ALLOW_DATA_ATTR: true,
    ALLOW_UNKNOWN_PROTOCOLS: false,
  });
};

/**
 * Verifica se uma URL contém algum dos domínios listados
 * @param {string} url - URL do script a ser verificada
 * @param {Array} domains - Lista de domínios para verificar
 * @returns {boolean} - true se a URL contém algum domínio da lista
 */
const isAbsoluteOrSchemeRelativeUrl = (url) => {
  if (typeof url !== 'string') return false;
  const trimmed = url.trim();
  return trimmed.startsWith('//') || /^https?:\/\//i.test(trimmed);
};

const normalizeDomainToHostname = (domain) => {
  if (typeof domain !== 'string') return null;
  const trimmed = domain.trim();
  if (!trimmed) return null;

  // Remove esquema (se houver) e qualquer path/query/hash
  const withoutScheme = trimmed
    .replace(/^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//, '')
    .replace(/^\/\//, '');
  const host = withoutScheme.split('/')[0].split('?')[0].split('#')[0];
  return host || null;
};

const hostnameMatchesDomain = (hostname, domainHostname) => {
  if (!hostname || !domainHostname) return false;
  if (hostname === domainHostname) return true;
  return hostname.endsWith(`.${domainHostname}`);
};

/**
 * Verifica se uma URL pertence a algum dos domínios listados.
 * Importante: comparação por hostname (não substring) para evitar bypass do tipo
 * "www.gov.br.evil.com" ou "evil.com?redirect=www.gov.br".
 */
const containsDomain = (url, domains = []) => {
  if (!isBrowser() || !url || !Array.isArray(domains)) return false;
  if (!isAbsoluteOrSchemeRelativeUrl(url)) return false;

  let hostname;
  try {
    hostname = new URL(url, window.location.origin).hostname;
  } catch (e) {
    return false;
  }

  const normalizedDomains = domains
    .map(normalizeDomainToHostname)
    .filter(Boolean);

  return normalizedDomains.some((domainHostname) =>
    hostnameMatchesDomain(hostname, domainHostname),
  );
};

/**
 * Extrai URLs de scripts de um código HTML/JavaScript
 * @param {string} code - Código HTML/JavaScript contendo scripts
 * @returns {Array} - Lista de URLs encontradas
 */
const extractScriptUrls = (code) => {
  if (!code) return [];

  const urls = [];

  // Regex para encontrar src= especificamente em tags <script ...>
  // (evita false positives em <img src=...>, <iframe src=...>, etc.)
  const srcRegex = /<script\b[^>]*\bsrc=["']([^"']+)["'][^>]*>/gi;
  let match;

  while ((match = srcRegex.exec(code)) !== null) {
    urls.push(match[1]);
  }

  // Regex para encontrar URLs em strings JavaScript (http://, https://, //)
  const urlRegex = /(https?:)?\/\/[^\s"'<>)]+/gi;
  const urlMatches = code.match(urlRegex) || [];
  urls.push(...urlMatches);

  return [...new Set(urls)]; // Remove duplicatas
};

const canRenderWebstats = ({ webstatsJs, thirdPartyCookies, groupDomains }) => {
  const { thirdParty = [], noConsentUrls = [] } = groupDomains ?? {};
  const scriptUrls = extractScriptUrls(webstatsJs);

  // Lógica de renderização:
  // 1. Se não há URLs externas, renderiza sem consentimento (ex: scripts inline ou sem src)
  // 2. Se TODAS as URLs estão em noConsentUrls -> renderiza sem consentimento
  // 3. Se há URLs em thirdParty e/ou noConsentUrls -> só renderiza com consentimento
  // 4. URLs desconhecidas nao são renderizadas

  if (scriptUrls.length === 0) return true;

  const allUrlsInNoConsent = scriptUrls.every((url) =>
    containsDomain(url, noConsentUrls),
  );
  if (allUrlsInNoConsent) return true;

  const allUrlsKnown = scriptUrls.every(
    (url) =>
      containsDomain(url, noConsentUrls) || containsDomain(url, thirdParty),
  );
  if (!allUrlsKnown) return false;

  return thirdPartyCookies === true;
};

const copyAttributesSafely = (fromEl, toEl) => {
  Array.from(fromEl.attributes).forEach((attr) => {
    try {
      toEl.setAttribute(attr.name, attr.value);
    } catch (e) {
      // Proteção apenas contra atributo inválido (não trata erro de runtime do script)
    }
  });
};

const renderWebstats = ({ container, webstatsJs }) => {
  if (!isBrowser() || !container) return;

  // Evita reinjetar o mesmo snippet repetidas vezes (ex.: re-renders do React,
  // atualizações redundantes do Redux, etc). Isso reduz a chance de inicializar
  // widgets (chat) múltiplas vezes.
  const nextHash = hashString(webstatsJs);
  const prevHash = container?.dataset?.webstatsHash;
  if (prevHash === nextHash && container.childNodes?.length > 0) {
    return;
  }

  container.innerHTML = '';
  if (container?.dataset) {
    container.dataset.webstatsHash = nextHash;
  }

  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = webstatsJs;

  // Captura scripts (inclusive aninhados) para evitar duplicação quando clonar HTML.
  const scriptEls = Array.from(tempDiv.querySelectorAll('script'));

  // Injeta scripts primeiro
  scriptEls.forEach((oldScript) => {
    const newScript = document.createElement('script');

    copyAttributesSafely(oldScript, newScript);

    newScript.addEventListener('error', () => {
      // intencionalmente vazio: só evita que algum handler externo quebre o fluxo
    });

    if (oldScript.textContent) {
      // Em ambiente de testes (jsdom), scripts inline são executados ao serem inseridos
      // e podem quebrar os testes com SyntaxError/throw; em produção, o navegador só
      // registra no console. Aqui preservamos o conteúdo, mas evitamos execução no teste.
      if (isTestEnvironment()) {
        newScript.type = 'text/plain';
      }

      newScript.textContent = oldScript.textContent;
    }

    try {
      container.appendChild(newScript);
    } catch (e) {
      // console.warn('WebStatsRenderer: Erro ao injetar script de webstats', e);
    }
  });

  // Remove scripts para não cloná-los novamente no HTML abaixo
  scriptEls.forEach((s) => s.parentNode?.removeChild(s));

  // Sanitiza e injeta o restante do HTML (sem scripts)
  const sanitized = sanitizeNonScriptHtml(tempDiv.innerHTML);
  tempDiv.innerHTML = sanitized;

  Array.from(tempDiv.childNodes).forEach((node) => {
    container.appendChild(node.cloneNode(true));
  });
};

/**
 * WebStatsRenderer - Renderiza scripts de webstats (como chatbots) respeitando as regras LGPD
 *
 * Este componente:
 * - Busca o campo webstats_js do controlpanel do Plone via @govsite
 * - Verifica se os domínios dos scripts estão permitidos conforme LGPD
 * - Renderiza o script apenas no cliente (SSR safe)
 * - Respeita as preferências de cookies do usuário
 *
 * @param {boolean} thirdPartyCookies - Se cookies de terceiros estão permitidos
 * @param {boolean} hasLgpdCookie - Se o usuário já aceitou/rejeitou cookies
 * @param {boolean} isClient - Se está rodando no cliente (vs servidor)
 * @param {Object} groupDomains - Objeto com listas de domínios permitidos/proibidos
 */
const WebStatsRenderer = ({
  thirdPartyCookies,
  hasLgpdCookie,
  isClient,
  groupDomains = {},
}) => {
  const scriptRef = useRef(null);

  // Seleciona os dados do site properties que contém webstats_js
  const siteProperties = useSelector((state) => state.site_properties);
  const webstatsJs = siteProperties?.data?.webstats_js || '';

  const shouldRender = useMemo(() => {
    if (!isClient || !webstatsJs || !hasLgpdCookie) return false;
    return canRenderWebstats({ webstatsJs, thirdPartyCookies, groupDomains });
  }, [isClient, webstatsJs, hasLgpdCookie, thirdPartyCookies, groupDomains]);

  // Renderiza o script quando aprovado
  useEffect(() => {
    if (!shouldRender || !webstatsJs || !scriptRef.current || !isBrowser()) {
      return;
    }

    const container = scriptRef.current;
    renderWebstats({ container, webstatsJs });

    // Cleanup function
    return () => {
      if (container) {
        container.innerHTML = '';
        try {
          if (container?.dataset) {
            delete container.dataset.webstatsHash;
          }
        } catch (e) {
          // noop
        }
      }
    };
  }, [shouldRender, webstatsJs]);

  // Não renderiza nada no servidor ou se não deve renderizar
  if (!isClient || !shouldRender) {
    return null;
  }

  return <div ref={scriptRef} id="webstats-renderer" />;
};

export default withLgpdCookie(WebStatsRenderer);
